import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { ClipboardList, Package, Phone, ChevronDown, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import AdminTopBar from "@/components/AdminTopBar";

const statusStyle = {
  Ordered: "bg-[#F8DAD7] text-[#A01F1F]",
  Packed: "bg-[#fef9c3] text-[#a16207]",
  Shipped: "bg-[#e9d5ff] text-[#7e22ce]",
  Delivered: "bg-[#dcfce7] text-[#15803d]",
  Cancelled: "bg-[#fee2e2] text-[#b91c1c]",
};

const AdminOrders = () => {
  const { token, isAdmin } = useAdmin();
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);
  const [shipping, setShipping] = useState({}); // orderId -> {busy, tracking}

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const createShipment = async (order) => {
    setShipping((s) => ({ ...s, [order.id]: { ...s[order.id], busy: true } }));
    try {
      const { data } = await http.post(`/admin/orders/${order.id}/ship`, {}, auth);
      setOrders((prev) => prev.map((o) =>
        o.id === order.id ? { ...o, waybill: data.waybill, shipping_status: data.shipping_status, status: "Packed" } : o));
      toast.success(`Shipment created · AWB ${data.waybill}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create shipment");
    } finally {
      setShipping((s) => ({ ...s, [order.id]: { ...s[order.id], busy: false } }));
    }
  };

  const trackShipment = async (order) => {
    setShipping((s) => ({ ...s, [order.id]: { ...s[order.id], busy: true } }));
    try {
      const { data } = await http.get(`/admin/orders/${order.id}/tracking`, auth);
      setShipping((s) => ({ ...s, [order.id]: { busy: false, tracking: data } }));
      toast.success(`Status: ${data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tracking unavailable");
      setShipping((s) => ({ ...s, [order.id]: { ...s[order.id], busy: false } }));
    }
  };

  const load = () =>
    http.get("/admin/orders", auth).then((r) => {
      setOrders(r.data.orders);
      setStatuses(r.data.statuses);
      setLoading(false);
    });

  useEffect(() => {
    if (isAdmin) load().catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [isAdmin]);

  const stats = useMemo(() => ({
    total: orders.length,
    revenue: orders.filter((o) => o.status !== "Cancelled").reduce((s, o) => s + o.payable, 0),
    pending: orders.filter((o) => ["Ordered", "Packed"].includes(o.status)).length,
    delivered: orders.filter((o) => o.status === "Delivered").length,
  }), [orders]);

  const filtered = filter === "All" ? orders : orders.filter((o) => o.status === filter);

  const changeStatus = async (order, status) => {
    try {
      await http.patch(`/admin/orders/${order.id}/status`, { status }, auth);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
      toast.success(`Order ${order.id} → ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  return (
    <main className="min-h-[80vh] bg-[#f8fafc]" data-testid="admin-orders-page">
      <AdminTopBar />
      <div className="max-w-[1400px] mx-auto px-4 py-5 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Total Orders", value: stats.total, cls: "text-[#0f172a]" },
            { label: "Revenue", value: formatPrice(stats.revenue), cls: "text-[#15803d]" },
            { label: "Pending", value: stats.pending, cls: "text-[#a16207]" },
            { label: "Delivered", value: stats.delivered, cls: "text-[#C62828]" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-sm border border-[#e2e8f0] p-4">
              <p className="text-xs text-[#64748b] uppercase tracking-wide">{s.label}</p>
              <p className={`font-head text-2xl font-bold mt-1 ${s.cls}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-sm border border-[#e2e8f0] p-3 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#475569]">Filter:</span>
          {["All", ...statuses].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 h-8 rounded-sm border transition-colors ${
                filter === s ? "bg-[#C62828] text-white border-[#C62828]" : "border-[#e2e8f0] text-[#475569] hover:bg-slate-50"
              }`}
              data-testid={`order-filter-${s.toLowerCase()}`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-sm border border-[#e2e8f0] py-16 text-center text-[#64748b]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-sm border border-[#e2e8f0] py-16 flex flex-col items-center gap-3 text-[#64748b]" data-testid="admin-no-orders">
            <ClipboardList className="w-12 h-12 text-[#cbd5e1]" />
            No orders yet.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((o) => (
              <div key={o.id} className="bg-white rounded-sm border border-[#e2e8f0] overflow-hidden" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold text-[#0f172a] text-sm">{o.id}</p>
                    <p className="text-xs text-[#64748b]">
                      {o.address?.name} · +91 {o.phone} · {new Date(o.created_at).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="text-sm">
                    <span className="text-[#64748b]">{o.items.length} item(s)</span>
                    <span className="font-semibold text-[#0f172a] ml-3">{formatPrice(o.payable)}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-sm ${statusStyle[o.status] || ""}`} data-testid={`order-status-badge-${o.id}`}>
                    {o.status}
                  </span>
                  <div className="relative">
                    <select
                      value={o.status}
                      onChange={(e) => changeStatus(o, e.target.value)}
                      className="appearance-none text-sm border border-[#e2e8f0] rounded-sm pl-3 pr-8 h-9 outline-none focus:border-[#C62828] bg-white"
                      data-testid={`order-status-select-${o.id}`}
                    >
                      {statuses.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-[#64748b] pointer-events-none" />
                  </div>
                  <button
                    onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                    className="text-sm text-[#C62828] font-medium px-2"
                    data-testid={`order-toggle-${o.id}`}
                  >
                    {expanded === o.id ? "Hide" : "Details"}
                  </button>
                </div>

                {expanded === o.id && (
                  <div className="border-t border-[#e2e8f0] bg-[#f8fafc] p-4 grid grid-cols-1 md:grid-cols-2 gap-4 fk-fade-up">
                    <div>
                      <p className="text-xs font-semibold text-[#64748b] uppercase mb-2 flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" /> Items
                      </p>
                      {o.items.map((it) => (
                        <div key={it.id} className="flex items-center gap-2 text-sm py-1">
                          <img src={it.image_url} alt="" className="w-8 h-8 object-contain bg-white border border-[#e2e8f0] rounded-sm" />
                          <span className="flex-1 line-clamp-1">{it.name}</span>
                          <span className="text-[#64748b]">×{it.qty}</span>
                          <span className="font-medium">{formatPrice(it.price * it.qty)}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#64748b] uppercase mb-2 flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" /> Delivery
                      </p>
                      <p className="text-sm text-[#334155] leading-relaxed">
                        {[o.address?.address, o.address?.locality].filter(Boolean).join(", ")}<br />
                        {o.address?.city}, {o.address?.state} - {o.address?.pincode}<br />
                        Payment: {o.payment_method} ({o.payment_status}) · Est: {o.est_delivery}
                      </p>

                      <p className="text-xs font-semibold text-[#64748b] uppercase mt-4 mb-2 flex items-center gap-1">
                        <Truck className="w-3.5 h-3.5" /> Delhivery Shipping
                      </p>
                      {o.waybill ? (
                        <div className="text-sm text-[#334155]">
                          <p data-testid={`awb-${o.id}`}>
                            AWB: <b>{o.waybill}</b>
                            {o.shipping_status && <span className="ml-2 text-[#7e22ce]">{o.shipping_status}</span>}
                          </p>
                          <button
                            onClick={() => trackShipment(o)}
                            disabled={shipping[o.id]?.busy}
                            className="mt-2 flex items-center gap-1.5 text-sm font-medium text-[#C62828] border border-[#C62828]/30 px-3 h-8 rounded-sm hover:bg-[#FCEBEA] disabled:opacity-60"
                            data-testid={`track-shipment-${o.id}`}
                          >
                            <MapPin className="w-3.5 h-3.5" /> {shipping[o.id]?.busy ? "Tracking…" : "Track"}
                          </button>
                          {shipping[o.id]?.tracking && (
                            <div className="mt-2 border-l-2 border-[#e2e8f0] pl-3 space-y-1">
                              {(shipping[o.id].tracking.scans || []).slice(0, 5).map((s, i) => (
                                <p key={i} className="text-xs text-[#475569]">
                                  <span className="font-medium">{s.scan}</span> · {s.location} · {s.time}
                                </p>
                              ))}
                              {(!shipping[o.id].tracking.scans || shipping[o.id].tracking.scans.length === 0) && (
                                <p className="text-xs text-[#94a3b8]">No scans yet · {shipping[o.id].tracking.status}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => createShipment(o)}
                          disabled={shipping[o.id]?.busy || o.payment_status !== "paid"}
                          className="flex items-center gap-1.5 bg-[#7e22ce] text-white text-sm font-semibold px-4 h-9 rounded-sm hover:bg-[#6b21a8] disabled:opacity-60"
                          data-testid={`create-shipment-${o.id}`}
                        >
                          <Truck className="w-4 h-4" /> {shipping[o.id]?.busy ? "Creating…" : "Create Delhivery Shipment"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminOrders;
